import { FIG_D1, evaluate, type ExtentCurve } from './charts';
import { q, type Recorder } from './trace';

export type ExtentStatus = 'in-range' | 'below-range' | 'above-range';

export interface ChartExtent {
  distance: number | null;
  status: ExtentStatus;
  curve: ExtentCurve;
}

/**
 * Hazardous distance from Fig. D.1. Below the drawn line the curve's starting distance is used,
 * as E.2 Ex. 4 does ("the chart is not valid for less than 1 m, the zone is assessed as 1 m").
 * Above it there is no value: the chart is not to be extrapolated (D.3, MD-002).
 */
export function chartExtent(qc: number, curve: ExtentCurve): ChartExtent {
  const c = FIG_D1[curve];
  if (qc < c.qcMin) return { distance: evaluate(c, c.qcMin), status: 'below-range', curve };
  if (qc > c.qcMax) return { distance: null, status: 'above-range', curve };
  return { distance: evaluate(c, qc), status: 'in-range', curve };
}

export function recordChartExtent(qc: number, curve: ExtentCurve, rec: Recorder, purpose: 'horizontal' | 'vertical'): ChartExtent {
  const e = chartExtent(qc, curve);
  const notes: NonNullable<Parameters<Recorder['step']>[0]['notes']> = [];
  if (e.status === 'below-range') {
    notes.push({ kind: 'decision', text: 'Qc is below the start of the line; the line\'s starting distance is used (as in E.2 Example 4).', ref: { clause: 'D.3', md: 'MD-002' } });
  }
  if (e.status === 'above-range') {
    notes.push({ kind: 'warning', text: 'Qc is beyond the end of the line. The chart must not be extrapolated; use another method and enter the extent.', ref: { clause: 'D.3', md: 'MD-002' } });
  }
  if (purpose === 'vertical') {
    notes.push({ kind: 'decision', text: 'Heavy vapour: vertical extent from the diffusive line, horizontal from the heavy gas line (as read in E.2 Example 1).', ref: { clause: 'D.3', md: 'MD-004' } });
  }
  rec.step({
    id: `extent.${purpose}`,
    label: purpose === 'horizontal' ? `Hazardous distance (${curveName(curve)} line)` : `Vertical extent (${curveName(curve)} line)`,
    ref: { clause: 'D.3', figure: 'D.1' },
    inputs: { Qc: q(qc, 'm3/s') },
    output: e.distance === null ? { symbol: purpose === 'horizontal' ? 'r' : 'r (vertical)', text: 'Outside chart' } : { symbol: purpose === 'horizontal' ? 'r' : 'r (vertical)', ...q(e.distance, 'm') },
    ...(notes.length ? { notes } : {}),
  });
  return e;
}

export const curveName = (c: ExtentCurve): string => ({ jet: 'jet', diffusive: 'diffusive', heavy: 'heavy gas' })[c];
